import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaWallet,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaRupeeSign,
  FaFileCsv,
  FaChevronDown,
  FaChevronRight,
  FaExternalLinkAlt,
  FaCopy,
  FaArrowDown,
  FaArrowUp,
  FaHourglassHalf,
  FaUserSlash,
  FaInbox,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCrown,
  FaMoneyBillWave,
  FaEnvelope,
  FaPhoneAlt,
  FaChevronLeft,
  FaBan,
  FaUnlock,
  FaTrash,
  FaExclamationTriangle,
  FaBroom,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes wlFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes wlFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes wlBar { from { width: 0; } }
@keyframes wlOpen { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
@keyframes wlShine { 0% { transform: translateX(-120%) skewX(-20deg); } 100% { transform: translateX(260%) skewX(-20deg); } }
.wl-in { animation: wlFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.wl-fade { animation: wlFade .2s ease both; }
.wl-bar { animation: wlBar .9s cubic-bezier(.2,.7,.2,1) both; }
.wl-open { animation: wlOpen .3s cubic-bezier(.2,.8,.2,1) both; }
.wl-shine::after { content: ""; position: absolute; top: 0; bottom: 0; width: 35%; background: linear-gradient(90deg, transparent, rgba(255,255,255,.18), transparent); animation: wlShine 3.5s ease-in-out infinite; }
.wl-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.wl-press:hover { transform: translateY(-1px); }
.wl-press:active { transform: scale(.96); }
.wl-row { transition: background-color .15s ease; }
.wl-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
.wl-seg { transition: filter .2s ease, transform .2s ease; }
.wl-seg:hover { filter: brightness(1.1); transform: scaleY(1.15); }
@media (prefers-reduced-motion: reduce) {
  .wl-in, .wl-fade, .wl-bar, .wl-open { animation: none; }
  .wl-shine::after { animation: none; display: none; }
  .wl-press:hover, .wl-press:active, .wl-seg:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500";

// Balance groups
const BUCKETS = [
  { id: "zero", label: "₹0", test: (b) => b <= 0, color: "bg-slate-300 dark:bg-slate-600" },
  { id: "small", label: "Under ₹10", test: (b) => b > 0 && b < 10, color: "bg-emerald-200" },
  { id: "mid", label: "₹10 – ₹100", test: (b) => b >= 10 && b < 100, color: "bg-emerald-400" },
  { id: "big", label: "₹100 – ₹1,000", test: (b) => b >= 100 && b < 1000, color: "bg-emerald-500" },
  { id: "huge", label: "₹1,000+", test: (b) => b >= 1000, color: "bg-emerald-700" },
];

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const ago = (d) => {
  if (!d) return "never";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  const days = Math.floor(s / 86400);
  if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
  const m = Math.floor(days / 30);
  return `${m} month${m > 1 ? "s" : ""} ago`;
};
const nm = (s) => (s && String(s).trim() ? String(s).trim() : null);

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="wl-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Initials = ({ name, size = 36 }) => {
  const t = nm(name)
    ? nm(name)
        .split(/\s+/)
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : null;
  return (
    <span
      className={`rounded-full shrink-0 flex items-center justify-center font-semibold ${t ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-slate-100 text-slate-400 dark:bg-slate-700"}`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {t || <FaUserSlash size={size * 0.4} />}
    </span>
  );
};

/* ================= page ================= */

const WalletList = () => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [pending, setPending] = useState({}); // user_id -> {count, amount}
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [bucket, setBucket] = useState("all");
  const [flag, setFlag] = useState("all"); // all | pending | deleted
  const [sort, setSort] = useState({ field: "balance", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [expanded, setExpanded] = useState(null);
  const [peek, setPeek] = useState({}); // user_id -> data | 'loading' | 'error'
  const [userStatus, setUserStatus] = useState({}); // user_id -> account_status
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [checked, setChecked] = useState([]);
  const [confirm, setConfirm] = useState(null); // {type: 'block'|'unblock'|'delete', list}
  const [deleteText, setDeleteText] = useState("");
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/wallet.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  // Account actions live in users.php (same "block-user" action as the All Users page)
  const postUsers = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/users.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    try {
      const [w, p, u] = await Promise.all([
        post("walletList"),
        post("withdrawRequestList", { status: "pending" }).catch(() => null),
        postUsers("all-users").catch(() => null),
      ]);
      if (Array.isArray(u)) {
        const sm = {};
        u.forEach((x) => {
          sm[String(x.user_id)] = x.status || "Live";
        });
        setUserStatus(sm);
        setUsersLoaded(true);
      } else {
        setUsersLoaded(false);
      }
      setWallets(Array.isArray(w?.wallets) ? w.wallets : []);
      const map = {};
      (p?.withdrawals || []).forEach((r) => {
        const k = String(r.user_id);
        if (!map[k]) map[k] = { count: 0, amount: 0 };
        map[k].count += 1;
        map[k].amount += num(r.amount);
      });
      setPending(map);
      setPeek({});
    } catch (err) {
      console.error("API Error:", err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load wallets" });
    }
    setLoading(false);
  }, [post, postUsers]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  // A wallet whose user no longer exists
  const gone = useCallback(
    (w) => !nm(w.name) || (usersLoaded && !(String(w.user_id) in userStatus)),
    [usersLoaded, userStatus]
  );
  const label = (w) => (gone(w) ? "Unknown user" : nm(w.name));
  const blocked = (w) => String(userStatus[String(w.user_id)] || "").toLowerCase() === "block";

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, list } = confirm;
    const status = type === "block" ? "Block" : type === "unblock" ? "Live" : "Delete";
    setWorking(true);
    let ok = 0;
    for (const w of list) {
      try {
        const res = await postUsers("block-user", { user_id: w.user_id, status });
        if (res && res.status === false) throw new Error("Failed");
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setDeleteText("");
    setChecked([]);
    setExpanded(null);
    const verb = status === "Block" ? "blocked" : status === "Live" ? "unblocked" : "removed";
    if (ok) toast.current?.show({ severity: "success", summary: "Done", detail: `${ok} ${ok === 1 ? "account" : "accounts"} ${verb}` });
    if (ok < list.length) toast.current?.show({ severity: "error", summary: "Error", detail: `${list.length - ok} could not be ${verb}` });
    fetchWallets();
  };

  const loadPeek = async (user_id) => {
    if (peek[user_id] && peek[user_id] !== "error") return;
    setPeek((p) => ({ ...p, [user_id]: "loading" }));
    try {
      const res = await post("getWalletData", { user_id });
      setPeek((p) => ({ ...p, [user_id]: res?.status ? res.data : "error" }));
    } catch (e) {
      setPeek((p) => ({ ...p, [user_id]: "error" }));
    }
  };

  const toggleExpand = (user_id) => {
    if (expanded === user_id) setExpanded(null);
    else {
      setExpanded(user_id);
      loadPeek(user_id);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape") {
        setExpanded(null);
        setConfirm((c) => (working ? c : null));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [working]);

  /* ----- derived ----- */

  const q = query.trim();

  const rankMap = useMemo(() => {
    const m = {};
    [...wallets].sort((a, b) => num(b.balance) - num(a.balance)).forEach((w, i) => (m[w.user_id] = i + 1));
    return m;
  }, [wallets]);

  const maxBal = useMemo(() => Math.max(1, ...wallets.map((w) => num(w.balance))), [wallets]);

  const stats = useMemo(() => {
    const total = wallets.reduce((s, w) => s + num(w.balance), 0);
    const pendAmt = Object.values(pending).reduce((s, p) => s + p.amount, 0);
    const pendCount = Object.values(pending).reduce((s, p) => s + p.count, 0);
    const deleted = wallets.filter(gone);
    return {
      total,
      count: wallets.length,
      withMoney: wallets.filter((w) => num(w.balance) > 0).length,
      avg: wallets.length ? total / wallets.length : 0,
      pendAmt,
      pendCount,
      deletedCount: deleted.length,
      deletedMoney: deleted.reduce((s, w) => s + num(w.balance), 0),
    };
  }, [wallets, pending, gone]);

  const bucketStats = useMemo(
    () =>
      BUCKETS.map((b) => {
        const list = wallets.filter((w) => b.test(num(w.balance)));
        return { ...b, count: list.length, amount: list.reduce((s, w) => s + num(w.balance), 0) };
      }),
    [wallets]
  );

  const topHolders = useMemo(() => [...wallets].sort((a, b) => num(b.balance) - num(a.balance)).slice(0, 5), [wallets]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const b = BUCKETS.find((x) => x.id === bucket);
    let l = wallets.filter((w) => {
      if (b && !b.test(num(w.balance))) return false;
      if (flag === "pending" && !pending[String(w.user_id)]) return false;
      if (flag === "deleted" && !gone(w)) return false;
      if (flag === "blocked" && !blocked(w)) return false;
      if (!ql) return true;
      return [label(w), w.user_id].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    l = [...l].sort((a, b2) => {
      let x;
      let y;
      if (sort.field === "balance") {
        x = num(a.balance);
        y = num(b2.balance);
      } else if (sort.field === "last_update") {
        x = parseAny(a.last_update)?.getTime() || 0;
        y = parseAny(b2.last_update)?.getTime() || 0;
      } else if (sort.field === "pending") {
        x = pending[String(a.user_id)]?.amount || 0;
        y = pending[String(b2.user_id)]?.amount || 0;
      } else {
        x = String(gone(a) ? "~" : nm(a.name)).toLowerCase();
        y = String(gone(b2) ? "~" : nm(b2.name)).toLowerCase();
      }
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
    return l;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallets, q, bucket, flag, sort, pending, gone, userStatus]);

  useEffect(() => setPage(1), [q, bucket, flag, sort, pageSize]);
  useEffect(() => {
    const valid = new Set(wallets.map((w) => w.user_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [wallets]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const filteredTotal = filtered.reduce((s, w) => s + num(w.balance), 0);

  const hasFilters = q || bucket !== "all" || flag !== "all";
  const clearFilters = () => {
    setQuery("");
    setBucket("all");
    setFlag("all");
  };

  const toggleSort = (field) => setSort((s) => ({ field, dir: s.field === field && s.dir === "desc" ? "asc" : "desc" }));

  const copyText = (text, label = "Copied") => {
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: label, detail: String(text), life: 1500 });
  };

  const exportCsv = () => {
    const head = ["Rank", "User ID", "Name", "Balance", "Pending Withdrawal", "Last Update"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = filtered.map((w) => [rankMap[w.user_id], w.user_id, label(w), num(w.balance), pending[String(w.user_id)]?.amount || 0, w.last_update].map(esc).join(","));
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-wallets-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const SortTh = ({ field, children, right }) => {
    const active = sort.field === field;
    const Icon = !active ? FaSort : sort.dir === "asc" ? FaSortUp : FaSortDown;
    return (
      <th className={`px-3 py-3 font-medium whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        <button type="button" onClick={() => toggleSort(field)} className="inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white">
          {children}
          <Icon size={10} className={active ? "text-emerald-600" : "text-slate-300"} />
        </button>
      </th>
    );
  };

  /* ----- peek panel ----- */

  const renderPeek = (w) => {
    const data = peek[w.user_id];
    if (!data || data === "loading") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-700 animate-pulse" />
          ))}
        </div>
      );
    }
    if (data === "error") {
      return (
        <div className="text-sm text-rose-600 flex items-center gap-2">
          Could not load this account.
          <button type="button" onClick={() => loadPeek(w.user_id)} className="underline font-medium">
            Try again
          </button>
        </div>
      );
    }
    const txns = data.transactions || [];
    const credit = txns.filter((t) => t.tnx_type === "credit").reduce((s, t) => s + num(t.amount), 0);
    const debit = txns.filter((t) => t.tnx_type !== "credit").reduce((s, t) => s + num(t.amount), 0);
    const pend = pending[String(w.user_id)];
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 space-y-2 text-sm">
          <p className="text-xs font-semibold text-slate-500">Contact</p>
          <p className="flex items-center gap-2 text-slate-700 dark:text-slate-200 break-all">
            <FaEnvelope className="text-slate-400 shrink-0" size={11} /> {data.user?.email_id || "-"}
          </p>
          <p className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
            <FaPhoneAlt className="text-slate-400 shrink-0" size={11} /> {data.user?.phone_num || "-"}
          </p>
          <button type="button" onClick={() => navigate(`/user-profile/${w.user_id}`)} className="text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1">
            User profile <FaExternalLinkAlt size={9} />
          </button>
        </div>

        <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-semibold text-slate-500">Money flow</p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 p-2.5">
              <p className="text-[11px] text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
                <FaArrowDown size={9} /> Total in
              </p>
              <p className="font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">{money(credit)}</p>
            </div>
            <div className="rounded-lg bg-rose-50 dark:bg-rose-900/20 p-2.5">
              <p className="text-[11px] text-rose-700 dark:text-rose-300 inline-flex items-center gap-1">
                <FaArrowUp size={9} /> Total out
              </p>
              <p className="font-bold text-rose-700 dark:text-rose-300 tabular-nums">{money(debit)}</p>
            </div>
          </div>
          {pend && (
            <button type="button" onClick={() => navigate("/wallet/withdrawal-req")} className="wl-press mt-2 w-full flex items-center justify-between rounded-lg bg-amber-50 dark:bg-amber-900/20 px-2.5 py-2 text-xs text-amber-800 dark:text-amber-200">
              <span className="inline-flex items-center gap-1.5">
                <FaHourglassHalf size={10} /> {pend.count} withdrawal{pend.count > 1 ? "s" : ""} waiting
              </span>
              <b className="tabular-nums">{money(pend.amount)}</b>
            </button>
          )}
          {pend && pend.amount > num(w.balance) && (
            <p className="mt-2 text-[11px] text-rose-600">Pending withdrawal is more than the balance. Accepting it will fail.</p>
          )}
        </div>

        <div className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-slate-500">Last transactions</p>
            <span className="text-[11px] text-slate-400">{txns.length} total</span>
          </div>
          <ul className="mt-2 space-y-1.5">
            {txns.slice(0, 4).map((t) => {
              const cr = t.tnx_type === "credit";
              return (
                <li key={t.tnx_id} className="flex items-center gap-2 text-xs">
                  <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${cr ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                    {cr ? <FaArrowDown size={9} /> : <FaArrowUp size={9} />}
                  </span>
                  <span className="flex-1 min-w-0 truncate text-slate-600 dark:text-slate-300" title={t.remark}>
                    {t.remark || t.tnx_type}
                  </span>
                  <b className={`tabular-nums ${cr ? "text-emerald-700" : "text-rose-700"}`}>
                    {cr ? "+" : "-"}
                    {money(t.amount)}
                  </b>
                </li>
              );
            })}
            {!txns.length && <li className="text-xs text-slate-400">No transactions yet</li>}
          </ul>
          <button type="button" onClick={() => navigate(`/wallet-account/${w.user_id}`)} className="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1">
            See full account <FaExternalLinkAlt size={9} />
          </button>
        </div>
      </div>
    );
  };

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="wl-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Finance</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Wallet Management</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Group member earnings. See who holds how much and what is waiting to be paid out.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate("/wallet/withdrawal-req")} className="wl-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaMoneyBillWave size={12} /> Withdrawal requests
            {stats.pendCount > 0 && <span className="min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center">{stats.pendCount}</span>}
          </button>
          <button type="button" onClick={exportCsv} disabled={!filtered.length} className="wl-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50">
            <FaFileCsv /> Export
          </button>
          <button type="button" onClick={fetchWallets} disabled={loading} className="wl-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Vault hero */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="wl-in wl-shine relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white p-6 xl:col-span-2">
          <div className="pointer-events-none absolute -right-10 -top-10 w-64 h-64 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="inline-flex items-center gap-2 text-sm text-emerald-100/80">
                <FaWallet /> Money held in all wallets
              </p>
              <p className="mt-1 text-4xl sm:text-5xl font-black tracking-tight tabular-nums">{loading && !wallets.length ? "…" : money(stats.total)}</p>
              <p className="mt-1 text-sm text-emerald-100/70">
                {stats.withMoney} of {stats.count} wallets have money • average {money(stats.avg)}
              </p>
            </div>
            <div className="rounded-2xl bg-white/10 border border-white/10 px-4 py-3 min-w-[190px]">
              <p className="text-xs text-emerald-100/80 inline-flex items-center gap-1.5">
                <FaHourglassHalf size={10} /> Waiting to be paid out
              </p>
              <p className="text-2xl font-bold tabular-nums">{money(stats.pendAmt)}</p>
              <p className="text-[11px] text-emerald-100/70">{stats.pendCount} withdrawal request{stats.pendCount === 1 ? "" : "s"}</p>
            </div>
          </div>

          {/* Distribution */}
          <div className="relative mt-6">
            <p className="text-xs text-emerald-100/80 mb-2">Wallets by balance (tap to filter)</p>
            <div className="flex h-4 rounded-full overflow-hidden bg-white/10 gap-0.5">
              {bucketStats.map((b) =>
                b.count ? (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBucket(bucket === b.id ? "all" : b.id)}
                    title={`${b.label}: ${b.count} wallets`}
                    className={`wl-seg wl-bar h-full ${b.color} ${bucket !== "all" && bucket !== b.id ? "opacity-30" : ""}`}
                    style={{ width: `${(b.count / Math.max(1, stats.count)) * 100}%` }}
                  />
                ) : null
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {bucketStats.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setBucket(bucket === b.id ? "all" : b.id)}
                  className={`wl-press inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-semibold border ${bucket === b.id ? "bg-white text-emerald-900 border-white" : "bg-white/5 border-white/15 text-white/90 hover:bg-white/10"}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${b.color}`} />
                  {b.label}
                  <span className="opacity-70 tabular-nums">{b.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Top holders + alerts */}
        <div className={`${CARD} wl-in p-5`} style={{ animationDelay: "80ms" }}>
          <div className="flex items-center gap-2">
            <FaCrown className="text-amber-500" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Top wallets</h2>
          </div>
          <ul className="mt-3 space-y-2">
            {topHolders.map((w, i) => (
              <li key={w.user_id}>
                <button type="button" onClick={() => navigate(`/wallet-account/${w.user_id}`)} className="wl-press w-full flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/40 text-left">
                  <span className={`w-5 text-xs font-bold text-center ${i === 0 ? "text-amber-500" : "text-slate-400"}`}>{i + 1}</span>
                  <Initials name={w.name} size={30} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${!gone(w) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>{label(w)}</p>
                    <div className="h-1 mt-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                      <div className="wl-bar h-full rounded-full bg-emerald-500" style={{ width: `${(num(w.balance) / maxBal) * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400 tabular-nums">{money(w.balance)}</span>
                </button>
              </li>
            ))}
            {!topHolders.length && <li className="text-sm text-slate-500">No wallets yet</li>}
          </ul>
          {stats.deletedCount > 0 && (
            <button type="button" onClick={() => setFlag("deleted")} className="wl-press mt-4 w-full flex items-start gap-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 p-3 text-left text-xs text-rose-900 dark:text-rose-200">
              <FaUserSlash className="mt-0.5 shrink-0" />
              <span>
                <b>{stats.deletedCount}</b> wallet{stats.deletedCount > 1 ? "s belong" : " belongs"} to users who no longer exist, holding <b>{money(stats.deletedMoney)}</b>. Tap to review.
              </span>
            </button>
          )}
          {stats.deletedCount > 0 && (
            <button
              type="button"
              onClick={() => {
                setDeleteText("");
                setConfirm({ type: "delete", list: wallets.filter(gone) });
              }}
              className="wl-press mt-2 w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
            >
              <FaBroom size={12} /> Clean up all {stats.deletedCount} unknown wallets
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className={`${CARD} wl-in mt-4 p-3 flex flex-wrap items-center gap-3`} style={{ animationDelay: "160ms" }}>
        <div className="relative flex-1 min-w-[240px]">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
          <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or user ID" className={`${INPUT} w-full h-10 pl-10 pr-10`} />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            {query ? (
              <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                <FaTimes size={12} />
              </button>
            ) : (
              <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
            )}
          </div>
        </div>
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
          {[
            { id: "all", label: "All wallets" },
            { id: "pending", label: `Withdrawal pending (${Object.keys(pending).length})` },
            { id: "deleted", label: `Unknown users (${stats.deletedCount})` },
            { id: "blocked", label: "Blocked" },
          ].map((f) => (
            <button key={f.id} type="button" onClick={() => setFlag(f.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${flag === f.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
              {f.label}
            </button>
          ))}
        </div>
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="text-sm font-medium text-emerald-700 dark:text-emerald-400 hover:underline">
            Clear all
          </button>
        )}
      </div>

      {/* Bulk bar */}
      {checked.length > 0 && (
        <div className="wl-fade mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2.5 text-sm">
          <span className="font-semibold text-emerald-900 dark:text-emerald-100 mr-2">
            {checked.length} selected ({money(wallets.filter((w) => checked.includes(w.user_id)).reduce((s, w) => s + num(w.balance), 0))})
          </span>
          <button
            type="button"
            onClick={() => setConfirm({ type: "block", list: wallets.filter((w) => checked.includes(w.user_id) && !gone(w) && !blocked(w)) })}
            disabled={!wallets.some((w) => checked.includes(w.user_id) && !gone(w) && !blocked(w))}
            className="wl-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-40"
          >
            <FaBan size={11} /> Block
          </button>
          <button
            type="button"
            onClick={() => setConfirm({ type: "unblock", list: wallets.filter((w) => checked.includes(w.user_id) && blocked(w)) })}
            disabled={!wallets.some((w) => checked.includes(w.user_id) && blocked(w))}
            className="wl-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40"
          >
            <FaUnlock size={11} /> Unblock
          </button>
          <button
            type="button"
            onClick={() => {
              setDeleteText("");
              setConfirm({ type: "delete", list: wallets.filter((w) => checked.includes(w.user_id)) });
            }}
            className="wl-press inline-flex items-center gap-1.5 h-9 px-3.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold"
          >
            <FaTrash size={11} /> Delete
          </button>
          <button type="button" onClick={() => setChecked([])} className="ml-auto text-slate-600 dark:text-slate-300 hover:underline">
            Clear selection
          </button>
        </div>
      )}

      {/* Table */}
      <div className={`${CARD} wl-in mt-4 overflow-hidden`} style={{ animationDelay: "200ms" }}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-sm">
          <p className="text-slate-600 dark:text-slate-300">
            <b>{filtered.length}</b> wallet{filtered.length === 1 ? "" : "s"} {q && <>for "<b>{q}</b>"</>} holding <b className="text-emerald-700 dark:text-emerald-400">{money(filteredTotal)}</b>
          </p>
          <p className="text-xs text-slate-400">Click a row to peek inside</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="pl-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={pageRows.length > 0 && pageRows.every((w) => checked.includes(w.user_id))}
                    onChange={() => {
                      const ids = pageRows.map((w) => w.user_id);
                      const all = ids.every((id) => checked.includes(id));
                      setChecked((c) => (all ? c.filter((id) => !ids.includes(id)) : Array.from(new Set([...c, ...ids]))));
                    }}
                    className="w-4 h-4 accent-emerald-600 cursor-pointer"
                    aria-label="Select all on this page"
                  />
                </th>
                <th className="py-3 w-6" />
                <th className="px-3 py-3 font-medium text-left w-12">#</th>
                <SortTh field="name">User</SortTh>
                <SortTh field="balance">Balance</SortTh>
                <SortTh field="pending">Withdrawal</SortTh>
                <SortTh field="last_update">Last Update</SortTh>
                <th className="px-3 py-3 pr-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && !wallets.length &&
                [0, 1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td colSpan={8} className="px-4 py-2">
                      <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />
                    </td>
                  </tr>
                ))}
              {!loading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center">
                    <FaInbox className="mx-auto text-slate-300" size={32} />
                    <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">{wallets.length ? "No wallet matches these filters" : "No wallets yet"}</p>
                    {hasFilters && (
                      <button type="button" onClick={clearFilters} className="mt-2 text-sm text-emerald-700 font-medium hover:underline">
                        Clear all filters
                      </button>
                    )}
                  </td>
                </tr>
              )}
              {pageRows.map((w) => {
                const open = expanded === w.user_id;
                const bal = num(w.balance);
                const d = parseAny(w.last_update);
                const pend = pending[String(w.user_id)];
                const rank = rankMap[w.user_id];
                return (
                  <React.Fragment key={w.user_id}>
                    <tr onClick={() => toggleExpand(w.user_id)} className={`wl-row cursor-pointer border-b border-slate-100 dark:border-slate-700 ${open ? "bg-emerald-50/60 dark:bg-emerald-900/10" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}>
                      <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked.includes(w.user_id)}
                          onChange={() => setChecked((c) => (c.includes(w.user_id) ? c.filter((x) => x !== w.user_id) : [...c, w.user_id]))}
                          className="w-4 h-4 accent-emerald-600 cursor-pointer"
                          aria-label="Select wallet"
                        />
                      </td>
                      <td className="py-3 text-slate-400">{open ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex min-w-[26px] h-6 px-1 items-center justify-center rounded-md text-xs font-bold tabular-nums ${rank <= 3 && bal > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"}`}>{rank}</span>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3 min-w-[200px]">
                          <Initials name={w.name} />
                          <div className="min-w-0">
                            <p className={`font-medium truncate flex items-center gap-1.5 ${!gone(w) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>
                              <Hi text={label(w)} q={q} />
                              {gone(w) && <span className="not-italic text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">NOT FOUND</span>}
                              {blocked(w) && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">BLOCKED</span>}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyText(w.user_id, "User ID copied");
                              }}
                              className="text-xs text-slate-500 tabular-nums hover:text-slate-800 inline-flex items-center gap-1"
                            >
                              <Hi text={w.user_id} q={q} /> <FaCopy size={8} />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 min-w-[180px]">
                        <p className={`font-bold tabular-nums ${bal > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-slate-400"}`}>{money(bal)}</p>
                        <div className="h-1.5 mt-1 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden max-w-[160px]">
                          <div className="wl-bar h-full rounded-full bg-emerald-500" style={{ width: `${(bal / maxBal) * 100}%` }} />
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {pend ? (
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${pend.amount > bal ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`} title={pend.amount > bal ? "More than the balance" : ""}>
                            <FaHourglassHalf size={9} /> {money(pend.amount)}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">None</span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-slate-700 dark:text-slate-200">{ago(d)}</div>
                        <div className="text-xs text-slate-500">
                          {fmtDate(d)}, {fmtTime(d)}
                        </div>
                      </td>
                      <td className="px-3 py-3 pr-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1.5">
                          {!gone(w) && (
                            <button
                              type="button"
                              onClick={() => navigate(`/wallet-account/${w.user_id}`)}
                              className="wl-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-semibold whitespace-nowrap"
                            >
                              <FaRupeeSign size={9} /> View Account
                            </button>
                          )}
                          {!gone(w) && (
                            <button
                              type="button"
                              onClick={() => setConfirm({ type: blocked(w) ? "unblock" : "block", list: [w] })}
                              title={blocked(w) ? "Unblock user" : "Block user"}
                              aria-label={blocked(w) ? "Unblock user" : "Block user"}
                              className={`wl-press w-8 h-8 rounded-lg flex items-center justify-center ${blocked(w) ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                            >
                              {blocked(w) ? <FaUnlock size={11} /> : <FaBan size={11} />}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteText("");
                              setConfirm({ type: "delete", list: [w] });
                            }}
                            title={gone(w) ? "Remove this wallet" : "Delete user and wallet"}
                            aria-label="Delete"
                            className={`wl-press h-8 rounded-lg flex items-center justify-center gap-1.5 text-xs font-semibold ${gone(w) ? "px-3 bg-rose-600 hover:bg-rose-700 text-white" : "w-8 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white"}`}
                          >
                            <FaTrash size={10} /> {gone(w) && "Remove"}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-emerald-50/40 dark:bg-emerald-900/10 border-b border-slate-100 dark:border-slate-700">
                        <td colSpan={8} className="px-4 py-4">
                          <div className="wl-open">{renderPeek(w)}</div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              Rows per page
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={`${INPUT} h-8 px-2`}>
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="tabular-nums text-slate-600 dark:text-slate-300">
                {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
              </span>
              <div className="flex gap-1">
                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="wl-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
                  <FaChevronLeft size={10} />
                </button>
                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="wl-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40" aria-label="Next page">
                  <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* ================= Confirm ================= */}
      {confirm && confirm.list.length > 0 && (() => {
        const list = confirm.list;
        const unknown = list.filter(gone);
        const real = list.filter((w) => !gone(w));
        const total = list.reduce((s, w) => s + num(w.balance), 0);
        const isDelete = confirm.type === "delete";
        const ready = !isDelete || deleteText.trim().toUpperCase() === "DELETE";
        return (
          <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
            <div className="wl-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
            <div className="wl-in relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
              <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${confirm.type === "unblock" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
                {isDelete ? <FaExclamationTriangle size={18} /> : confirm.type === "unblock" ? <FaUnlock size={16} /> : <FaBan size={16} />}
              </span>
              <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
                {isDelete
                  ? list.length === 1
                    ? gone(list[0])
                      ? "Remove this unknown wallet?"
                      : `Delete ${label(list[0])} completely?`
                    : `Delete ${list.length} accounts?`
                  : confirm.type === "block"
                  ? list.length === 1
                    ? `Block ${label(list[0])}?`
                    : `Block ${list.length} users?`
                  : list.length === 1
                  ? `Unblock ${label(list[0])}?`
                  : `Unblock ${list.length} users?`}
              </h3>
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-1 space-y-1">
                {isDelete ? (
                  <>
                    {unknown.length > 0 && <p>{unknown.length} wallet{unknown.length > 1 ? "s" : ""} of users who no longer exist will be cleaned up with their leftover transactions and withdrawal requests.</p>}
                    {real.length > 0 && (
                      <p className="text-rose-600 font-medium">
                        {real.length} real user account{real.length > 1 ? "s" : ""} will be deleted completely: wallet, points, posts, groups, withdrawals and everything else.
                      </p>
                    )}
                    <p>This cannot be undone.</p>
                  </>
                ) : confirm.type === "block" ? (
                  <p>They cannot use the app and get an 'Account Suspended' notification. Their wallet money stays as it is.</p>
                ) : (
                  <p>They can use the app again and get an 'Account Activated' notification.</p>
                )}
              </div>

              <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
                {list.slice(0, 30).map((w) => (
                  <div key={w.user_id} className="flex items-center gap-3 p-2.5">
                    <Initials name={gone(w) ? null : w.name} size={28} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${gone(w) ? "text-slate-400 italic" : "text-slate-900 dark:text-white"}`}>{label(w)}</p>
                      <p className="text-xs text-slate-500 tabular-nums">{w.user_id}</p>
                    </div>
                    <span className="text-sm font-bold tabular-nums text-emerald-700">{money(w.balance)}</span>
                  </div>
                ))}
                {list.length > 30 && <p className="p-2.5 text-xs text-slate-500">and {list.length - 30} more</p>}
              </div>

              {isDelete && total > 0 && (
                <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-xs p-3">
                  <FaExclamationTriangle className="mt-0.5 shrink-0" /> {money(total)} in these wallets will be wiped out.
                </p>
              )}

              {isDelete && (
                <div className="mt-4">
                  <label htmlFor="wl-del" className="text-sm text-slate-700 dark:text-slate-200">
                    Type <b>DELETE</b> to confirm
                  </label>
                  <input id="wl-del" value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus placeholder="DELETE" className={`${INPUT} w-full h-10 px-3 mt-1.5`} />
                </div>
              )}

              <div className="flex gap-2 mt-5">
                <button type="button" onClick={() => setConfirm(null)} disabled={working} className="wl-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={runConfirm}
                  disabled={working || !ready}
                  className={`wl-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${confirm.type === "unblock" ? "bg-emerald-600 hover:bg-emerald-700" : confirm.type === "block" ? "bg-amber-600 hover:bg-amber-700" : "bg-rose-600 hover:bg-rose-700"}`}
                >
                  {working && <FaSyncAlt className="animate-spin" size={12} />}
                  {working ? "Please wait..." : isDelete ? "Delete forever" : confirm.type === "block" ? "Yes, block" : "Yes, unblock"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default WalletList;